import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { skipToken } from '@reduxjs/toolkit/query';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CloseIcon from '@mui/icons-material/Close';

import { captureError } from '@clatter/platform';
import { IconButtonRounded, NgErrorMessage } from '../UI';
import { addNotification } from '../../store';
import { useAssetDownload, useUpload } from '../../hooks';
import { NgFileUpload } from '../NgFileUpload/NgFileUpload';
import { useGetAssetThumbnailUrlQuery } from '../../services/assets.service';

const StyledLayout = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.medium};
  align-items: flex-start;
`;

const StyledThumbnail = styled.div`
  flex-shrink: 0;

  img {
    width: 248px;
    height: 154px;
    object-fit: cover;
    border-radius: ${({ theme }) => theme.radius.small};
    border: 1px solid ${({ theme }) => theme.palette.border};
    display: block;
  }
`;

const StyledInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const StyledFileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.medium};
  margin-bottom: ${({ theme }) => theme.spacing.small};

  a {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }
  }

  .actions {
    display: flex;
    gap: ${({ theme }) => theme.spacing.small};
  }
`;

const StyledUploadWrapper = styled.div`
  position: relative;
`;

const StyledCancelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.small};

  span {
    font-size: ${({ theme }) => theme.fontSize.sm};
    font-weight: 600;
  }
`;

interface Props {
  asset: any;
  uploadFolderId: number | undefined;
  isLoading?: boolean;
  acceptedFileTypes: string;
  helperText?: string;
  uploadTitle?: string;
  downloadTitle?: string;
  onUpload?: (data: any) => void;
}

export const NgAssetUploadInput: FC<Props> = ({
  asset,
  uploadFolderId,
  acceptedFileTypes,
  uploadTitle,
  downloadTitle = 'Download',
  helperText,
  onUpload,
  isLoading,
}) => {
  const dispatch = useDispatch();
  const { uploadFiles } = useUpload();
  const { downloadAssetById } = useAssetDownload();
  const [showUpload, setShowUpload] = useState(false);

  const { data: thumbnailUrl } = useGetAssetThumbnailUrlQuery(asset?.ID ? { assetId: asset.ID } : skipToken);

  const handleUploadFile = async (file: any) => {
    try {
      const response = await uploadFiles({ file: file, folderId: uploadFolderId! });

      if (typeof onUpload === 'function') {
        onUpload(response);
      }
      setShowUpload(false);
    } catch (error: any) {
      captureError(new Error('Error uploading file'), { component: 'NgAssetUploadInput', skipConsole: true });
    }
  };

  const handleDownloadFile = async (e: any) => {
    if (e) {
      e.preventDefault();
    }

    if (!asset?.ID) {
      dispatch(addNotification({ type: 'error', message: 'Couldn\'t download the template file.' }));
      return;
    }

    downloadAssetById({ assetId: asset.ID });
  };

  const hasAsset = !!asset;

  const uploadArea = uploadFolderId ? (
    <NgFileUpload
      isLoading={isLoading}
      title={uploadTitle}
      onUpload={handleUploadFile}
      targetId={uploadFolderId}
      acceptedFileTypes={acceptedFileTypes}
    />
  ) : (
    <NgErrorMessage message="Error: no upload folder ID provided!" logError />
  );

  if (hasAsset && thumbnailUrl) {
    return (
      <StyledLayout>
        <StyledThumbnail>
          <img src={thumbnailUrl} alt={asset?.DisplayName || 'Asset preview'} />
        </StyledThumbnail>

        <StyledInfo>
          <StyledFileRow onClick={handleDownloadFile}>
            <a href="#">{asset?.DisplayName}</a>
            <div className="actions">
              <IconButtonRounded title={downloadTitle} icon={<CloudDownloadOutlinedIcon />} filled materialIcon />
              {!showUpload && (
                <IconButtonRounded
                  title="Replace thumbnail"
                  icon={<FileUploadOutlinedIcon />}
                  filled
                  materialIcon
                  onClick={(e: any) => {
                    e.stopPropagation();
                    setShowUpload(true);
                  }}
                />
              )}
            </div>
          </StyledFileRow>

          {showUpload && (
            <StyledUploadWrapper>
              <StyledCancelRow>
                <span>Replace thumbnail</span>
                <IconButtonRounded
                  title="Cancel"
                  icon={<CloseIcon />}
                  materialIcon
                  onClick={() => setShowUpload(false)}
                />
              </StyledCancelRow>
              {helperText && <small>{helperText}</small>}
              {uploadArea}
            </StyledUploadWrapper>
          )}
        </StyledInfo>
      </StyledLayout>
    );
  }

  return (
    <>
      {asset && (
        <StyledFileRow onClick={handleDownloadFile}>
          <a href="#">{asset?.DisplayName}</a>

          <div className="actions">
            <IconButtonRounded title={downloadTitle} icon={<CloudDownloadOutlinedIcon />} filled materialIcon />
            {!showUpload && (
              <IconButtonRounded
                title="Replace"
                icon={<FileUploadOutlinedIcon />}
                filled
                materialIcon
                onClick={(e: any) => {
                  e.stopPropagation();
                  setShowUpload(true);
                }}
              />
            )}
          </div>
        </StyledFileRow>
      )}

      {hasAsset && showUpload && (
        <StyledUploadWrapper>
          <StyledCancelRow>
            <span>Replace</span>
            <IconButtonRounded title="Cancel" icon={<CloseIcon />} materialIcon onClick={() => setShowUpload(false)} />
          </StyledCancelRow>
          {helperText && <small>{helperText}</small>}
          {uploadArea}
        </StyledUploadWrapper>
      )}

      {!hasAsset && (
        <>
          {helperText && <small>{helperText}</small>}
          {uploadArea}
        </>
      )}
    </>
  );
};
